'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import type { RangeTile } from '@/lib/home-v2/data';
import { Btn, SectionHeading, WRAP, fmt, formatCount, type Labels } from './ui';

export interface RoomTab {
  id: string;
  label: string;
  image: string;
  description: string;
  tiles: RangeTile[];
  cta?: { label: string; href: string };
}

/** "Design your room": pick Kitchen / Wardrobe / Living Room, see the materials used there. */
export function RoomDesigner({ title, subtitle, rooms, num, labels }: { title: string; subtitle: string; rooms: RoomTab[]; num: string; labels: Labels }) {
  const [active, setActive] = useState(0);
  const room = rooms[active] ?? rooms[0];
  return (
    <section className={`${WRAP} pt-12 md:pt-20`} aria-labelledby="room-designer">
      <SectionHeading num={num} title={<span id="room-designer">{title}</span>} subtitle={subtitle} />
      <div role="tablist" aria-label="Rooms" className="scrollbar-hide mt-6 flex gap-2 overflow-x-auto md:mt-8 md:pl-[var(--hv-indent)]">
        {rooms.map((r, k) => (
          <button key={r.id} role="tab" aria-selected={k === active} aria-controls={`room-${r.id}`} onClick={() => setActive(k)}
            className={`flex items-center gap-2 whitespace-nowrap rounded-xl px-5 py-2.5 text-[15px] transition ${k === active ? 'bg-hv-ink font-semibold text-white' : 'border border-hv-line bg-white hover:border-hv-ink'}`}>
            {r.label}
            {labels.showDesignCounts && <span className={`font-hv-mono text-[11px] ${k === active ? 'text-hv-yellow' : 'text-hv-soft'}`}>{r.tiles.length}</span>}
          </button>
        ))}
      </div>

      <div id={`room-${room.id}`} role="tabpanel" className="mt-4 grid gap-3 md:mt-5 md:grid-cols-[1fr_1.5fr] md:gap-5 md:pl-[var(--hv-indent)]">
        <div className="relative min-h-[260px] overflow-hidden rounded-2xl md:min-h-[520px]">
          <Image key={room.image} src={room.image} alt={room.label} fill sizes="(min-width: 768px) 520px, 100vw" className="object-cover" style={{ animation: 'hvFade .4s ease both' }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute inset-x-5 bottom-5 flex flex-col gap-2 text-white">
            <div className="text-[28px] font-semibold leading-none md:text-[40px]">{room.label}</div>
            {room.description && <p className="max-w-[420px] text-sm text-white/85 md:text-base">{room.description}</p>}
            {room.cta && <div className="pt-1"><Btn href={room.cta.href} kind="white" arrow>{room.cta.label}</Btn></div>}
          </div>
        </div>
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {room.tiles.map((t, k) => (
            <li key={`${room.id}-${t.handle}`} style={{ animation: 'hvFade .4s ease both', animationDelay: `${k * 35}ms` }}>
              <Link href={`/${t.handle}`} className="group flex h-full flex-col gap-2 rounded-xl border border-hv-line bg-white p-2 transition hover:border-hv-ink">
                <div className="relative aspect-square overflow-hidden rounded-lg bg-hv-card">
                  {t.image && <Image src={t.image} alt={t.name} fill sizes="(min-width: 768px) 180px, 45vw" className="object-cover transition duration-500 group-hover:scale-105" />}
                </div>
                <div className="px-1 text-[13px] font-semibold leading-tight md:text-sm">{t.name}</div>
                {labels.showDesignCounts && <div className="mt-auto px-1 pb-1 font-hv-mono text-[10px] uppercase text-hv-soft">{fmt(labels.designs, { count: formatCount(t.count) })}</div>}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
